
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChartConfiguration } from 'chart.js';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';
import { getResourcesNew } from '../../../../framework/client/resource';

import {
    barChartOptions,
    chartBackgroundColors,
    getMonthLabelsNew,
    getTitleOptions,
    numberTooltipCallbacks,
} from '../../../service/report_service';
import { VehicleUsage } from '../../../../../common/application/model/VehicleUsage';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { VehicleContract } from '../../../../../common/application/model/VehicleContract';
import { formatNumber } from '../../../service/helper_service';
import { formatMonthStrings } from '../../../../../common/framework/util/time';
import { dateToDbMonthDateString, getLocalDate } from '../../../../../common/framework/util/convert';
import { P_PAGE_SIZE } from '../../../../../common/framework/constants';

@Component
export default class VehicleMonthlyCO2Chart extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_USAGE;

    @Prop(String) readonly vehicleContractId!: string;
    @Prop(Array) readonly customerIds!: string[];
    @Prop(Date) readonly lastMonth!: Date;
    @Prop(Array) readonly vehicleUsageData!: VehicleUsage[];

    shared = sharedState;

    local = {
        vehicleMonthlyMileageChart: undefined as Chart | undefined,
        chartData: [] as VehicleUsage[],
        fakeChartData: [] as any[],
        vehicleContractIds: [] as string[],
        vehicleContracts: [{ id: 1, vehicleCo2EmissionWltp: 124 } as any as VehicleContract] as VehicleContract[],
        months: [] as string[],
    };

    async mounted() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyCo2Chart();
    }

    @Watch('customerIds')
    async customerIdsChanged() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyCo2Chart();
    }

    @Watch('lastMonth')
    async lastMonthChanged() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyCo2Chart();
    }

    @Watch('vehicleUsageData')
    async vehicleUsageDataChanged() {
        await this.loadAsyncData();
        await this.addVehicleMonthlyCo2Chart();
    }

    private async loadAsyncData() {
        const beginDate = DateTime.fromJSDate(this.lastMonth || new Date())
            .minus({ months: 11 })
            .toJSDate();
        this.local.months = formatMonthStrings(beginDate.getFullYear(), beginDate.getMonth(), 11);

        if (this.vehicleUsageData) {
            this.local.chartData = [...this.vehicleUsageData];
            await this.getVehicleContracts();
        } else if (this.vehicleContractId) {
            const vehicleUsageParameters = new Map<string, string>([
                ['vehicleContractId', this.vehicleContractId],
                ['page', '1'],
                ['sortField', 'vehicleUsageDate'],
                ['sortOrder', 'desc'],
                [P_PAGE_SIZE, '12'],
            ]);

            this.local.chartData = await getResourcesNew<VehicleUsage>(
                this.resourceType,
                parseInt(vehicleUsageParameters.get('page') || '1'),
                vehicleUsageParameters,
            );
        }

        if (this.local.chartData.length === 0) {
            this.generateFakeData();
        }
    }

    private async getVehicleContracts(): Promise<void> {
        this.local.vehicleContractIds = this.local.chartData.map((chartData) => chartData.vehicleContractId);

        if (!this.local.vehicleContractIds.length) {
            return;
        }

        const vehicleContractParameters = new Map<string, any>([
            ['id', this.local.vehicleContractIds],
            ['page', '1'],
            [P_PAGE_SIZE, '1000'],
        ]);

        this.local.vehicleContracts = await getResourcesNew<VehicleContract>(
            ApplicationResource.VEHICLE_CONTRACT,
            parseInt(vehicleContractParameters.get('page') || '1'),
            vehicleContractParameters,
        );
    }

    private async addVehicleMonthlyCo2Chart() {
        const canvasContext = (document.getElementById('vehicle-monthly-co2-chart') as HTMLCanvasElement)!!.getContext(
            '2d',
        )!!;

        const chartConfiguration: ChartConfiguration = {
            type: 'bar',
            data: {
                labels: getMonthLabelsNew(this, this.local.months),
                datasets: this.chartDataSets,
            },
            options: {
                ...barChartOptions,
                plugins: {
                    ...barChartOptions.plugins,
                    title: getTitleOptions(`${this.$t(this.titleString)} [kg]`),
                    tooltip: {
                        callbacks: { ...numberTooltipCallbacks },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            callback: (value) => formatNumber(value as number),
                        },
                    },
                },
            },
        };

        this.local.vehicleMonthlyMileageChart?.destroy();
        this.local.vehicleMonthlyMileageChart = new Chart(canvasContext, chartConfiguration);
    }

    private get titleString(): string {
        if (this.vehicleContractId) {
            return 'title.vehicleMonthlyCo2Chart';
        } else {
            return 'title.vehiclesMonthlyCo2Chart';
        }
    }

    private static get backgroundColors(): string[] {
        return chartBackgroundColors;
    }

    private get chartDataSets(): any[] {
        const dataSets: any[] = [
            {
                label: `${this.$t('label.monthlyValue')}`,
                data: [],
                backgroundColor: VehicleMonthlyCO2Chart.backgroundColors[13],
            },
            {
                label: `${this.$t('label.cumulativeValue')}`,
                data: [],
                type: 'line',
                borderColor: VehicleMonthlyCO2Chart.backgroundColors[29],
                backgroundColor: VehicleMonthlyCO2Chart.backgroundColors[29],
                fill: false,
            },
        ];

        let cumulativeValue = 0;
        let preCumulativeValue = 0;

        this.local.months.forEach((month, index) => {
            let chartDataToUse: VehicleUsage[] = [];

            if (this.local.chartData.length) {
                chartDataToUse = [...this.local.chartData];
            } else {
                chartDataToUse = [...this.local.fakeChartData];
            }

            const totalMonthlyCO2 = chartDataToUse
                .filter(
                    (vehicleUsage) =>
                        dateToDbMonthDateString(getLocalDate(new Date(vehicleUsage.vehicleUsageDate))) === month,
                )
                .map((vehicleUsage) => vehicleUsage.co2Emissions || 0)
                .reduce((a, b) => a + b, 0);

            if (dataSets[0] && dataSets[0].data) {
                dataSets[0].data.push(totalMonthlyCO2 ? totalMonthlyCO2 : 0);
            }

            const previousTotalMonthlyCO2 = chartDataToUse
                .filter(
                    (vehicleUsage) =>
                        dateToDbMonthDateString(getLocalDate(new Date(vehicleUsage.vehicleUsageDate))) ===
                        this.local.months[index - 1],
                )
                .map((vehicleUsage) => vehicleUsage.co2Emissions || 0)
                .reduce((a, b) => a + b, 0);

            if (dataSets[1] && dataSets[1].data) {
                cumulativeValue += totalMonthlyCO2;
                preCumulativeValue += previousTotalMonthlyCO2;
                dataSets[1].data.push(totalMonthlyCO2 ? cumulativeValue : preCumulativeValue);
            }
        });

        return dataSets;
    }

    private generateFakeData(): void {
        this.local.fakeChartData = [];

        this.local.months.forEach((month) => {
            this.local.fakeChartData.push({
                vehicleContractId: this.local.vehicleContracts[0].id,
                vehicleUsageDate: DateTime.fromFormat(month, 'yyyy-MM').toJSDate(),
                monthlyMileage: Math.floor(Math.random() * (2000 - 500 + 1) + 500),
            });
        });
    }
}
